import { useCallback, useSyncExternalStore } from 'react';
import { get, set } from '../../../../store/safeLocalStorage';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';

/**
 * The browser memory of the red "N failed" badge of the Work menu entry (it was on Assets and on
 * Posts until the Work item came back, owner 2026-10-11): the time of the last visit of the Work
 * page, the Posts page or a Kit tab that saw the failures of an artist. One key per user and artist, so a
 * shared browser does not mix two people. The key name comes from the old Work page, so a visit
 * of Work before 2026-10-09 still counts. `safeLocalStorage` wraps every read and write in
 * try/catch: with no storage, nothing is remembered and the badge shows the open failures.
 */
const seenKey = (userId: number, accountId: number): string => `m24KitFailuresSeen:${userId}:${accountId}`;

const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Milliseconds of the last visit that saw the failures of the artist; 0 = never. */
export function readFailuresSeenAt(userId: number, accountId: number): number {
  const value = get<unknown>(seenKey(userId, accountId));
  return typeof value === 'number' ? value : 0;
}

/** Stores the time of a visit. It never goes back in time. */
export function markFailuresSeen(userId: number, accountId: number, at: number = Date.now()): void {
  if (readFailuresSeenAt(userId, accountId) >= at) {
    return;
  }
  set(seenKey(userId, accountId), at);
  notify();
}

/** The time of the last visit that saw the failures of the artist, and `markSeen`. */
export function useFailuresSeen(accountId: number | null): { seenAt: number; markSeen: () => void } {
  const userId = useUserTokenDecoded()?.id ?? null;
  const seenAt = useSyncExternalStore(subscribe, () =>
    userId === null || accountId === null ? 0 : readFailuresSeenAt(userId, accountId)
  );
  const markSeen = useCallback(() => {
    if (userId !== null && accountId !== null) {
      markFailuresSeen(userId, accountId);
    }
  }, [accountId, userId]);
  return { seenAt, markSeen };
}
